import React, { useEffect, useRef, useState } from 'react';
import {
  getAfastamentos, getFuncionariosResumo, getDepartamentos,
  createAfastamento, updateAfastamento, deleteAfastamento,
} from '../services/api';

// ── Listas de motivos ─────────────────────────────────────────────────────────
const MOTIVOS_JUSTIFICADA = [
  { value: 'ACIDENTE_TRABALHO', label: 'Acidente de Trabalho' },
  { value: 'ACOMP_FILHO',       label: 'Acompanhamento Filho(a) Menor' },
  { value: 'AFASTAMENTO_INSS',  label: 'Afastamento INSS' },
  { value: 'ATESTADO_MEDICO',   label: 'Atestado Médico' },
  { value: 'CASAMENTO',         label: 'Casamento' },
  { value: 'CONSULTA_MEDICA',   label: 'Consulta Médica' },
  { value: 'CONV_JURADO',       label: 'Convocação para Jurado' },
  { value: 'DEPOR_JUSTICA',     label: 'Depor na Justiça' },
  { value: 'DOACAO_SANGUE',     label: 'Doação Voluntária de Sangue' },
  { value: 'ESTAGIO_PROVA',     label: 'Estágio - Semana de Prova' },
  { value: 'FALECIMENTO',       label: 'Falecimento de Parente' },
  { value: 'FERIAS',            label: 'Férias' },
  { value: 'GREVE_AUTORIZADA',  label: 'Greve Autorizada pela Justiça' },
  { value: 'LIC_ADOTANTE',      label: 'Licença Adotante' },
  { value: 'LIC_MATERNIDADE',   label: 'Licença Maternidade' },
  { value: 'LIC_MILITAR',       label: 'Serviço Militar Obrigatório' },
  { value: 'LIC_NOJO',          label: 'Licença Nojo (Luto)' },
  { value: 'LIC_PATERNIDADE',   label: 'Licença Paternidade' },
  { value: 'LIC_SAUDE',         label: 'Licença Saúde / Médica' },
  { value: 'MANDATO_SINDICAL',  label: 'Mandato Sindical' },
  { value: 'NASCIMENTO_FILHO',  label: 'Nascimento de Filho' },
  { value: 'PROVA_VESTIBULAR',  label: 'Prova Vestibular' },
  { value: 'SERVICO_ELEITORAL', label: 'Serviço Eleitoral / Júri' },
  { value: 'TITULO_ELEITOR',    label: 'Retirar Título Eleitor' },
  { value: 'OUTROS',            label: 'Outros' },
];

const MOTIVOS_INJUSTIFICADA = [
  { value: 'INJ_ACADEMICO',      label: 'Acadêmico' },
  { value: 'INJ_ALAGAMENTO',     label: 'Alagamento' },
  { value: 'INJ_ATRASO',         label: 'Atraso' },
  { value: 'INJ_AUTO_ESCOLA',    label: 'Auto Escola' },
  { value: 'INJ_CONTA_BANCARIA', label: 'Conta Bancária' },
  { value: 'INJ_DETRAN',         label: 'DETRAN' },
  { value: 'INJ_ESPORTE',        label: 'Esporte' },
  { value: 'INJ_FERIADO_ANTES',  label: 'Feriado Antes' },
  { value: 'INJ_FERIADO_DEPOIS', label: 'Feriado Depois' },
  { value: 'INJ_GREVE',          label: 'Greve' },
  { value: 'INJ_PESSOAL',        label: 'Pessoal' },
  { value: 'INJ_PONTO_FAC',      label: 'Ponto Facultativo' },
  { value: 'INJ_PROTESTO',       label: 'Protesto' },
  { value: 'INJ_RESCISAO',       label: 'Rescisão' },
  { value: 'INJ_SAIDA_ANT',      label: 'Saída Antecipada' },
  { value: 'INJ_SEM_ENERGIA',    label: 'Sem Energia' },
  { value: 'INJ_SEM_INTERNET',   label: 'Sem Internet' },
  { value: 'INJ_SUSPENSAO',      label: 'Suspensão Disciplinar' },
  { value: 'INJ_TROCA_JORNADA',  label: 'Troca Jornada' },
  { value: 'INJ_VIAGEM',         label: 'Viagem' },
  { value: 'INJ_OUTROS',         label: 'Outros' },
];

const TODOS_MOTIVOS = [...MOTIVOS_JUSTIFICADA, ...MOTIVOS_INJUSTIFICADA];
const motivoLabel = (v) => TODOS_MOTIVOS.find(m => m.value === v)?.label || v;

const COR_TIPO = {
  JUSTIFICADA:   { bg: '#fef9c3', color: '#854d0e' },
  INJUSTIFICADA: { bg: '#fee2e2', color: '#991b1b' },
};

const MOSTRAR_CID       = ['ACIDENTE_TRABALHO', 'AFASTAMENTO_INSS', 'LIC_SAUDE', 'ATESTADO_MEDICO'];
const MOSTRAR_BENEFICIO = ['AFASTAMENTO_INSS', 'ACIDENTE_TRABALHO', 'LIC_MATERNIDADE'];

// ── Helpers ───────────────────────────────────────────────────────────────────
const isoToBR = iso => { if (!iso || !iso.includes('-')) return iso; const [y, m, d] = iso.split('-'); return `${d}/${m}/${y}`; };
const brToISO = br  => { if (!br  || !br.includes('/'))  return br;  const [d, m, y] = br.split('/');  return `${y}-${m}-${d}`; };

function calcDias(di, df) {
  if (!di || !df) return '';
  const v = Math.max((new Date(df) - new Date(di)) / 86400000 + 1, 0);
  return v;
}

// ── Combobox de funcionário ───────────────────────────────────────────────────
function FuncCombobox({ funcs, value, onChange, placeholder = 'Digite o nome ou matrícula...' }) {
  const [texto, setTexto]   = useState('');
  const [aberto, setAberto] = useState(false);
  const ref = useRef();

  // Sincroniza texto quando value muda externamente (ex: ao abrir edição)
  useEffect(() => {
    if (value) {
      const f = funcs.find(f => String(f.id) === String(value));
      setTexto(f ? f.nome : '');
    } else {
      setTexto('');
    }
  }, [value, funcs]);

  // Fecha ao clicar fora
  useEffect(() => {
    const handler = e => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const filtrados = texto.length >= 1
    ? funcs.filter(f =>
        f.nome.toLowerCase().includes(texto.toLowerCase()) ||
        f.matricula.toLowerCase().includes(texto.toLowerCase())
      ).slice(0, 10)
    : [];

  function selecionar(f) {
    setTexto(f.nome);
    setAberto(false);
    onChange(f.id);
  }

  function handleChange(e) {
    setTexto(e.target.value);
    setAberto(true);
    if (!e.target.value) onChange('');
  }

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <div style={{ position: 'relative' }}>
        <i className="bi bi-search" style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', color: '#cbd5e1', fontSize: 'var(--fs-sm)', pointerEvents: 'none' }} />
        <input
          type="text"
          className="form-control"
          placeholder={placeholder}
          value={texto}
          onChange={handleChange}
          onFocus={() => texto.length >= 1 && setAberto(true)}
          style={{ paddingLeft: 32 }}
          autoComplete="off"
        />
        {texto && (
          <button type="button" onClick={() => { setTexto(''); onChange(''); setAberto(false); }}
            style={{ position: 'absolute', right: 8, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', color: '#cbd5e1', cursor: 'pointer', padding: 0, lineHeight: 1 }}>
            <i className="bi bi-x-lg" style={{ fontSize: 'var(--fs-xs)' }} />
          </button>
        )}
      </div>
      {aberto && filtrados.length > 0 && (
        <div style={{ position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', marginTop: 2, maxHeight: 220, overflowY: 'auto' }}>
          {filtrados.map(f => (
            <button key={f.id} type="button" onMouseDown={() => selecionar(f)}
              style={{ display: 'flex', alignItems: 'center', gap: 10, width: '100%', background: 'none', border: 'none', padding: '8px 12px', cursor: 'pointer', textAlign: 'left' }}
              onMouseEnter={e => e.currentTarget.style.background = '#f8fafc'}
              onMouseLeave={e => e.currentTarget.style.background = 'none'}
            >
              <div style={{ width: 28, height: 28, borderRadius: '50%', background: 'var(--primary)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 'var(--fs-2xs)', fontWeight: 800, color: '#fff', flexShrink: 0 }}>
                {f.nome.split(' ').slice(0, 2).map(p => p[0]).join('').toUpperCase()}
              </div>
              <div>
                <div style={{ fontSize: 'var(--fs-sm)', fontWeight: 600, color: '#1e293b' }}>{f.nome}</div>
                <div style={{ fontSize: 'var(--fs-2xs)', color: '#94a3b8' }}>#{f.matricula}</div>
              </div>
            </button>
          ))}
        </div>
      )}
      {aberto && texto.length >= 1 && filtrados.length === 0 && (
        <div style={{ position: 'absolute', zIndex: 1000, top: '100%', left: 0, right: 0, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, boxShadow: '0 4px 16px rgba(0,0,0,0.10)', marginTop: 2, padding: '10px 14px', fontSize: 'var(--fs-sm)', color: '#94a3b8' }}>
          Nenhum funcionário encontrado
        </div>
      )}
    </div>
  );
}

// Status de análise
const STATUS_ANALISE_COR = {
  PENDENTE:          { bg: '#fef9c3', color: '#854d0e', icon: 'bi-hourglass-split',    label: 'Aguardando Análise' },
  APROVADO:          { bg: '#f0fdf4', color: '#15803d', icon: 'bi-check-circle-fill',  label: 'Aprovado' },
  PARCIAL_ABONADO:   { bg: '#eff6ff', color: '#1d4ed8', icon: 'bi-shield-check',       label: 'Abonado' },
  PARCIAL_COMPENSAR: { bg: '#fff7ed', color: '#c2410c', icon: 'bi-arrow-repeat',       label: 'Compensar' },
};

// ── Form inicial ──────────────────────────────────────────────────────────────
const FORM0 = {
  funcionario: '', tipo: 'JUSTIFICADA', motivo: '',
  unidade: 'DIAS',
  data_inicio: '', hora_inicio: '',
  data_fim: '',   hora_fim: '',
  quantidade_horas: '',
  cid: '', numero_beneficio: '', observacao: '',
  documento: null,
};

// ── Componente principal ──────────────────────────────────────────────────────
export default function Afastamentos() {
  const [lista, setLista]             = useState([]);
  const [funcs, setFuncs]             = useState([]);
  const [depts, setDepts]             = useState([]);
  const [filtros, setFiltros]         = useState({ funcionario: '', departamento: '', tipo: '', data_inicio: '', data_fim: '' });
  const [form, setForm]               = useState(FORM0);
  const [editId, setEditId]           = useState(null);
  const [modal, setModal]             = useState(false);
  const [loading, setLoading]         = useState(false);
  const [erro, setErro]               = useState('');
  const [analise, setAnalise]         = useState(null);   // afastamento em análise pelo gestor
  const [analiseForm, setAnaliseForm] = useState({ horas_justificadas: '', observacao_gestor: '', decisao: '' });
  const [analiseLoading, setAnaliseLoading] = useState(false);

  useEffect(() => {
    getFuncionariosResumo().then(r => setFuncs(r.data)).catch(() => {});
    getDepartamentos({ ativo: true }).then(r => setDepts(r.data.results || r.data)).catch(() => {});
  }, []);
  useEffect(() => { carregar(); }, [filtros]); // eslint-disable-line

  const carregar = async () => {
    setLoading(true);
    try {
      const p = {
        ...filtros,
        data_inicio: filtros.data_inicio ? isoToBR(filtros.data_inicio) : '',
        data_fim:    filtros.data_fim    ? isoToBR(filtros.data_fim)    : '',
      };
      const r = await getAfastamentos(p);
      setLista(r.data.results || r.data);
    } finally { setLoading(false); }
  };

  // Funcionários filtrados pelo departamento selecionado no filtro
  const funcsFiltrados = filtros.departamento
    ? funcs.filter(f => String(f.departamento) === String(filtros.departamento))
    : funcs;

  const abrir = (a = null) => {
    if (a) {
      setForm({
        funcionario:      a.funcionario,
        tipo:             a.tipo,
        motivo:           a.motivo || '',
        unidade:          a.unidade || 'DIAS',
        data_inicio:      brToISO(a.data_inicio),
        hora_inicio:      a.hora_inicio || '',
        data_fim:         brToISO(a.data_fim) || '',
        hora_fim:         a.hora_fim || '',
        quantidade_horas: a.quantidade_horas || '',
        cid:              a.cid || '',
        numero_beneficio: a.numero_beneficio || '',
        observacao:       a.observacao || '',
        documento:        null,  // file re-upload optional on edit
      });
      setEditId(a.id);
    } else {
      setForm({ ...FORM0, funcionario: filtros.funcionario || '' });
      setEditId(null);
    }
    setErro(''); setModal(true);
  };

  const salvar = async (e) => {
    e.preventDefault(); setLoading(true);
    try {
      const fd = new FormData();
      const campos = {
        ...form,
        data_inicio: isoToBR(form.data_inicio),
        data_fim:    form.unidade === 'DIAS' ? isoToBR(form.data_fim) : isoToBR(form.data_inicio),
      };
      Object.entries(campos).forEach(([k, v]) => {
        if (k === 'documento') { if (v) fd.append(k, v); }
        else if (v !== null && v !== undefined) fd.append(k, v);
      });
      editId ? await updateAfastamento(editId, fd) : await createAfastamento(fd);
      setModal(false); carregar();
    } catch (err) {
      setErro(JSON.stringify(err.response?.data || 'Erro'));
    } finally { setLoading(false); }
  };

  const abrirAnalise = (a) => {
    setAnalise(a);
    setAnaliseForm({
      horas_justificadas: a.horas_justificadas || '',
      observacao_gestor:  a.observacao_gestor  || '',
      decisao: '',
    });
  };

  const salvarAnalise = async (decisao) => {
    if (!analise) return;
    setAnaliseLoading(true);
    try {
      const fd = new FormData();
      fd.append('status_analise', decisao);
      fd.append('horas_justificadas', analiseForm.horas_justificadas || '');
      fd.append('observacao_gestor', analiseForm.observacao_gestor || '');
      // Repassa campos obrigatórios para o PATCH não os perder
      fd.append('funcionario', analise.funcionario);
      fd.append('tipo', analise.tipo);
      fd.append('data_inicio', analise.data_inicio);
      await updateAfastamento(analise.id, fd);
      setAnalise(null);
      carregar();
    } catch (err) {
      alert('Erro ao salvar análise: ' + JSON.stringify(err.response?.data || err.message));
    } finally { setAnaliseLoading(false); }
  };

  const excluir = async (id) => {
    if (!window.confirm('Excluir este afastamento?')) return;
    await deleteAfastamento(id); carregar();
  };

  const corTipo = tipo => COR_TIPO[tipo] || { bg: '#f3f4f6', color: '#374151' };

  const diasCalc = calcDias(form.data_inicio, form.unidade === 'DIAS' ? form.data_fim : form.data_inicio);

  return (
    <div>
      {/* ── Header ── */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', paddingBottom: 16, marginBottom: 20, borderBottom: '1px solid #f1f5f9' }}>
        <div style={{ display: 'flex', alignItems: 'center' }}>
          <div style={{ width: 38, height: 38, background: '#eff6ff', border: '1px solid #bfdbfe', borderRadius: 10, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--primary)', fontSize: '1rem', marginRight: 14, flexShrink: 0 }}>
            <i className="bi bi-person-x-fill" />
          </div>
          <div>
            <div style={{ fontSize: 'var(--fs-2xs)', fontWeight: 600, color: 'var(--text-subtle)', textTransform: 'uppercase', letterSpacing: '0.5px', marginBottom: 3 }}>Gestão de Pessoal · Cadastros</div>
            <div style={{ fontSize: '1.3rem', fontWeight: 700, color: '#334155', letterSpacing: '-0.5px', lineHeight: 1.2 }}>Afastamentos</div>
          </div>
        </div>
        <button className="btn-primary-custom" onClick={() => abrir()}>
          <i className="bi bi-plus-lg" />Novo
        </button>
      </div>

      {/* ── Filtros ── */}
      <div className="filter-bar">
        {/* Área/Departamento */}
        <div style={{ minWidth: 160 }}>
          <label className="form-label">Área</label>
          <select className="form-select" value={filtros.departamento} onChange={e => setFiltros({ ...filtros, departamento: e.target.value, funcionario: '' })}>
            <option value="">Todas</option>
            {depts.map(d => <option key={d.id} value={d.id}>{d.nome}</option>)}
          </select>
        </div>
        {/* Funcionário — busca por texto */}
        <div style={{ flex: 2, minWidth: 200 }}>
          <label className="form-label">Funcionário</label>
          <FuncCombobox
            funcs={funcsFiltrados}
            value={filtros.funcionario}
            onChange={id => setFiltros(f => ({ ...f, funcionario: id }))}
            placeholder="Buscar por nome ou matrícula..."
          />
        </div>
        {/* Tipo */}
        <div style={{ minWidth: 150 }}>
          <label className="form-label">Tipo</label>
          <select className="form-select" value={filtros.tipo} onChange={e => setFiltros({ ...filtros, tipo: e.target.value })}>
            <option value="">Todos</option>
            <option value="JUSTIFICADA">Justificada</option>
            <option value="INJUSTIFICADA">Não Justificada</option>
          </select>
        </div>
        {/* Período */}
        <div style={{ minWidth: 130 }}>
          <label className="form-label">A partir de</label>
          <input type="date" className="form-control" value={filtros.data_inicio} onChange={e => setFiltros({ ...filtros, data_inicio: e.target.value })} />
        </div>
        <div style={{ minWidth: 130 }}>
          <label className="form-label">Até</label>
          <input type="date" className="form-control" value={filtros.data_fim} onChange={e => setFiltros({ ...filtros, data_fim: e.target.value })} />
        </div>
      </div>

      {/* ── Tabela ── */}
      <div className="card-ponto">
        <div className="table-responsive">
          <table className="table-ponto" style={{ width: '100%' }}>
            <thead>
              <tr>
                <th style={{ paddingLeft: 32 }}>Funcionário</th>
                <th>Tipo / Motivo</th>
                <th>Início</th>
                <th>Fim / Duração</th>
                <th>CID</th>
                <th>Nº INSS</th>
                <th>Observação</th>
                <th style={{ width: 80 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={8} className="text-center py-3">
                  <span className="spinner-border spinner-border-sm text-primary" />
                </td></tr>
              )}
              {!loading && lista.map(a => {
                const cor = corTipo(a.tipo);
                return (
                  <tr key={a.id}>
                    <td style={{ maxWidth: 200, paddingLeft: 32 }}>
                      <div style={{ fontWeight: 600 }}>{a.funcionario_nome}</div>
                      <div style={{ fontSize: 'var(--fs-xs)', color: 'var(--text-muted)' }}>#{a.funcionario_matricula}</div>
                    </td>
                    <td>
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 5, flexWrap: 'wrap' }}>
                          <span style={{ background: cor.bg, color: cor.color, borderRadius: 3, padding: '1px 8px', fontSize: 'var(--fs-2xs)', fontWeight: 700 }}>
                            {a.tipo_display}
                          </span>
                          {a.tipo === 'INJUSTIFICADA' && (
                            <span title="Compensação necessária" style={{ background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 4, padding: '1px 6px', fontSize: 'var(--fs-2xs)', color: '#c2410c', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                              <i className="bi bi-arrow-repeat" />Comp.
                            </span>
                          )}
                          {a.tipo === 'JUSTIFICADA' && a.status_analise && STATUS_ANALISE_COR[a.status_analise] && (() => {
                            const s = STATUS_ANALISE_COR[a.status_analise];
                            return (
                              <span style={{ background: s.bg, color: s.color, borderRadius: 4, padding: '1px 6px', fontSize: 'var(--fs-2xs)', fontWeight: 700, display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                                <i className={`bi ${s.icon}`} />{s.label}
                              </span>
                            );
                          })()}
                        </div>
                        {a.motivo && (
                          <span style={{ fontSize: 'var(--fs-xs)', color: '#475569', fontWeight: 600 }}>
                            {a.motivo_display || motivoLabel(a.motivo)}
                          </span>
                        )}
                        {a.tipo === 'JUSTIFICADA' && a.documento && (
                          <a href={`http://localhost:8000${a.documento}`} target="_blank" rel="noreferrer"
                            style={{ fontSize: 'var(--fs-2xs)', color: '#2563eb', display: 'inline-flex', alignItems: 'center', gap: 3 }}>
                            <i className="bi bi-paperclip" />Ver documento
                          </a>
                        )}
                      </div>
                    </td>
                    <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {a.data_inicio}
                      {a.hora_inicio && <div style={{ fontSize: 'var(--fs-xs)' }}>{a.hora_inicio}</div>}
                    </td>
                    <td style={{ color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                      {a.unidade === 'HORAS'
                        ? <span style={{ fontWeight: 700, color: 'var(--primary)' }}>{a.quantidade_horas}h</span>
                        : <>
                            {a.data_fim}
                            {a.hora_fim && <div style={{ fontSize: 'var(--fs-xs)' }}>{a.hora_fim}</div>}
                            {a.dias > 0 && <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: 'var(--blue)' }}>{a.dias}d</div>}
                          </>
                      }
                    </td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>{a.cid || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', fontSize: 'var(--fs-xs)' }}>{a.numero_beneficio || '—'}</td>
                    <td style={{ color: 'var(--text-muted)', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{a.observacao || '—'}</td>
                    <td style={{ whiteSpace: 'nowrap' }}>
                      {a.tipo === 'JUSTIFICADA' && (
                        <button className="btn-icon" title="Analisar documento" style={{ color: '#15803d', marginRight: 6 }} onClick={() => abrirAnalise(a)}>
                          <i className="bi bi-clipboard2-check" />
                        </button>
                      )}
                      <button className="btn-icon" style={{ color: '#1d4ed8', marginRight: 6 }} onClick={() => abrir(a)}>
                        <i className="bi bi-pencil" />
                      </button>
                      <button className="btn-icon" style={{ color: '#dc2626' }} onClick={() => excluir(a.id)}>
                        <i className="bi bi-trash" />
                      </button>
                    </td>
                  </tr>
                );
              })}
              {!loading && lista.length === 0 && (
                <tr><td colSpan={8}>
                  <div className="empty-state"><i className="bi bi-person-x" /><p>Nenhum afastamento encontrado</p></div>
                </td></tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* ── Modal de Análise do Gestor ── */}
      {analise && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setAnalise(null)}>
          <div className="modal-box" style={{ maxWidth: 520 }}>
            <div className="modal-header-custom">
              <h5><i className="bi bi-clipboard2-check me-2" />Análise de Documento — Afastamento Justificado</h5>
              <button className="modal-close" onClick={() => setAnalise(null)}><i className="bi bi-x-lg" /></button>
            </div>
            <div className="modal-body-custom">
              {/* Info do afastamento */}
              <div style={{ background: '#f8fafc', borderRadius: 8, padding: '10px 14px', marginBottom: 14, fontSize: 'var(--fs-sm)' }}>
                <div style={{ fontWeight: 700, color: '#334155', marginBottom: 4 }}>{analise.funcionario_nome}</div>
                <div style={{ color: '#64748b' }}>
                  Período: <strong>{analise.data_inicio}</strong>
                  {analise.data_fim && analise.data_fim !== analise.data_inicio ? ` até ${analise.data_fim}` : ''}
                  {analise.dias > 0 ? ` (${analise.dias} dia${analise.dias > 1 ? 's' : ''})` : ''}
                  {analise.quantidade_horas ? ` — ${analise.quantidade_horas}h` : ''}
                </div>
                <div style={{ color: '#64748b', marginTop: 2 }}>Tipo: <strong>{analise.motivo_display || motivoLabel(analise.motivo)}</strong></div>
                {analise.documento && (
                  <a href={`http://localhost:8000${analise.documento}`} target="_blank" rel="noreferrer"
                    style={{ display: 'inline-flex', alignItems: 'center', gap: 4, marginTop: 6, fontSize: 'var(--fs-xs)', color: '#2563eb', fontWeight: 600 }}>
                    <i className="bi bi-paperclip" />Abrir documento anexado
                  </a>
                )}
                {!analise.documento && (
                  <div style={{ marginTop: 6, fontSize: 'var(--fs-xs)', color: '#dc2626' }}>
                    <i className="bi bi-exclamation-triangle me-1" />Nenhum documento foi anexado.
                  </div>
                )}
              </div>

              {/* Horas justificadas */}
              <div style={{ marginBottom: 12 }}>
                <label className="form-label">Horas/dias justificados pelo documento</label>
                <input type="number" className="form-control" min="0" step="0.5" placeholder="Ex: 4 (deixe em branco se cobre todo o período)"
                  value={analiseForm.horas_justificadas}
                  onChange={e => setAnaliseForm(f => ({ ...f, horas_justificadas: e.target.value }))}
                />
                <div style={{ fontSize: 'var(--fs-xs)', color: '#64748b', marginTop: 3 }}>
                  Deixe em branco para aprovação total do período.
                </div>
              </div>

              {/* Observação do gestor */}
              <div style={{ marginBottom: 14 }}>
                <label className="form-label">Observação do gestor</label>
                <textarea className="form-control" rows={2} placeholder="Ex: Atestado cobre apenas 4h — restante a compensar"
                  value={analiseForm.observacao_gestor}
                  onChange={e => setAnaliseForm(f => ({ ...f, observacao_gestor: e.target.value }))}
                />
              </div>

              {/* Decisão */}
              <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '12px 14px' }}>
                <div style={{ fontSize: 'var(--fs-xs)', fontWeight: 700, color: '#166534', marginBottom: 8 }}>Decisão do Gestor</div>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  <button type="button" disabled={analiseLoading}
                    style={{ flex: 1, minWidth: 120, background: '#f0fdf4', border: '2px solid #22c55e', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', color: '#15803d', fontSize: 'var(--fs-xs)', fontWeight: 700 }}
                    onClick={() => salvarAnalise('APROVADO')}>
                    <i className="bi bi-check-circle-fill me-1" />Aprovado — Período totalmente justificado
                  </button>
                  <button type="button" disabled={analiseLoading}
                    style={{ flex: 1, minWidth: 120, background: '#eff6ff', border: '2px solid #60a5fa', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', color: '#1d4ed8', fontSize: 'var(--fs-xs)', fontWeight: 700 }}
                    onClick={() => salvarAnalise('PARCIAL_ABONADO')}>
                    <i className="bi bi-shield-check me-1" />Abonar diferença
                  </button>
                  <button type="button" disabled={analiseLoading}
                    style={{ flex: 1, minWidth: 120, background: '#fff7ed', border: '2px solid #fb923c', borderRadius: 8, padding: '8px 10px', cursor: 'pointer', color: '#c2410c', fontSize: 'var(--fs-xs)', fontWeight: 700 }}
                    onClick={() => salvarAnalise('PARCIAL_COMPENSAR')}>
                    <i className="bi bi-arrow-repeat me-1" />Compensar diferença
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── Modal ── */}
      {modal && (
        <div className="modal-backdrop-custom" onClick={e => e.target === e.currentTarget && setModal(false)}>
          <div className="modal-box" style={{ maxWidth: 660 }}>
            <div className="modal-header-custom">
              <h5><i className="bi bi-person-x me-2" />{editId ? 'Editar Afastamento' : 'Novo Afastamento'}</h5>
              <button className="modal-close" onClick={() => setModal(false)}><i className="bi bi-x-lg" /></button>
            </div>
            <form onSubmit={salvar}>
              <div className="modal-body-custom">
                {erro && (
                  <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 12px', color: '#991b1b', fontSize: 'var(--fs-xs)', marginBottom: 12 }}>
                    {erro}
                  </div>
                )}
                <div className="row g-2">

                  {/* Funcionário com busca */}
                  <div className="col-12">
                    <label className="form-label">Funcionário *</label>
                    <FuncCombobox
                      funcs={funcs}
                      value={form.funcionario}
                      onChange={id => setForm(f => ({ ...f, funcionario: id }))}
                    />
                    {!form.funcionario && <div style={{ fontSize: 'var(--fs-xs)', color: '#dc2626', marginTop: 3 }}>Selecione um funcionário</div>}
                  </div>

                  {/* Tipo: Justificada / Não Justificada */}
                  <div className="col-md-5">
                    <label className="form-label">Tipo *</label>
                    <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                      {[
                        { v: 'JUSTIFICADA',   label: 'Justificada',     bg: '#fef9c3', color: '#854d0e' },
                        { v: 'INJUSTIFICADA', label: 'Não Justificada', bg: '#fee2e2', color: '#991b1b' },
                      ].map(op => (
                        <button key={op.v} type="button"
                          onClick={() => setForm(f => ({ ...f, tipo: op.v, motivo: '' }))}
                          style={{
                            flex: 1, padding: '7px 0', border: 'none', cursor: 'pointer',
                            fontSize: 'var(--fs-xs)', fontWeight: 700,
                            background: form.tipo === op.v ? op.bg : '#f8fafc',
                            color:      form.tipo === op.v ? op.color : '#94a3b8',
                            borderRight: op.v === 'JUSTIFICADA' ? '1px solid #e2e8f0' : 'none',
                            transition: 'all 0.15s',
                          }}
                        >{op.label}</button>
                      ))}
                    </div>
                  </div>

                  {/* Tipo de Ausência */}
                  <div className="col-md-7">
                    <label className="form-label">Tipo de Ausência *</label>
                    <select className="form-select" required value={form.motivo}
                      onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))}>
                      <option value="">Selecione...</option>
                      {(form.tipo === 'JUSTIFICADA' ? MOTIVOS_JUSTIFICADA : MOTIVOS_INJUSTIFICADA)
                        .map(m => <option key={m.value} value={m.value}>{m.label}</option>)}
                    </select>
                  </div>

                  {/* Aviso de compensação + upload de documento */}
                  <div className="col-12">
                    {form.tipo === 'JUSTIFICADA' ? (
                      <div style={{ background: '#f0fdf4', border: '1px solid #86efac', borderRadius: 8, padding: '10px 12px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 'var(--fs-xs)', color: '#15803d', fontWeight: 600, marginBottom: 8 }}>
                          <i className="bi bi-check-circle-fill" style={{ flexShrink: 0 }} />
                          Ausência justificada — anexe o documento comprobatório.
                        </div>
                        <label className="form-label" style={{ color: '#166534' }}>
                          <i className="bi bi-paperclip me-1" />Documento comprobatório
                        </label>
                        <input type="file" className="form-control form-control-sm"
                          accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                          onChange={e => setForm(f => ({ ...f, documento: e.target.files[0] || null }))}
                        />
                        {form.documento && (
                          <div style={{ fontSize: 'var(--fs-xs)', color: '#15803d', marginTop: 4 }}>
                            <i className="bi bi-file-earmark-check me-1" />{form.documento.name}
                          </div>
                        )}
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: 8, background: '#fff7ed', border: '1px solid #fed7aa', borderRadius: 8, padding: '8px 12px', fontSize: 'var(--fs-xs)', color: '#c2410c', fontWeight: 600 }}>
                        <i className="bi bi-arrow-repeat" style={{ flexShrink: 0 }} />
                        Ausência não justificada — o funcionário deverá compensar as horas/dias.
                      </div>
                    )}
                  </div>

                  {/* Unidade: Dias / Horas */}
                  <div className="col-12">
                    <label className="form-label">Duração em</label>
                    <div style={{ display: 'flex', borderRadius: 8, overflow: 'hidden', border: '1px solid #e2e8f0', width: 'fit-content' }}>
                      {[
                        { v: 'DIAS',  icon: 'bi-calendar-range', label: 'Dias',  bg: '#eff6ff', color: '#1d4ed8' },
                        { v: 'HORAS', icon: 'bi-clock',          label: 'Horas', bg: '#f0fdf4', color: '#15803d' },
                      ].map(op => (
                        <button key={op.v} type="button"
                          onClick={() => setForm(f => ({ ...f, unidade: op.v, data_fim: '', quantidade_horas: '' }))}
                          style={{
                            display: 'flex', alignItems: 'center', gap: 6,
                            padding: '6px 20px', border: 'none', cursor: 'pointer',
                            fontSize: 'var(--fs-sm)', fontWeight: 700,
                            background: form.unidade === op.v ? op.bg : '#f8fafc',
                            color:      form.unidade === op.v ? op.color : '#94a3b8',
                            borderRight: op.v === 'DIAS' ? '1px solid #e2e8f0' : 'none',
                            transition: 'all 0.15s',
                          }}
                        >
                          <i className={`bi ${op.icon}`} />{op.label}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Campos de data/hora conforme unidade */}
                  {form.unidade === 'DIAS' ? (
                    <>
                      <div className="col-md-3">
                        <label className="form-label">Início *</label>
                        <input type="date" className="form-control" required value={form.data_inicio}
                          onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Hora</label>
                        <input type="time" className="form-control" value={form.hora_inicio}
                          onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Término *</label>
                        <input type="date" className="form-control" required value={form.data_fim}
                          onChange={e => setForm(f => ({ ...f, data_fim: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Hora</label>
                        <input type="time" className="form-control" value={form.hora_fim}
                          onChange={e => setForm(f => ({ ...f, hora_fim: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Dias</label>
                        <input className="form-control" readOnly value={diasCalc} style={{ background: '#f8fafc', fontWeight: 700 }} />
                      </div>
                    </>
                  ) : (
                    <>
                      <div className="col-md-3">
                        <label className="form-label">Data *</label>
                        <input type="date" className="form-control" required value={form.data_inicio}
                          onChange={e => setForm(f => ({ ...f, data_inicio: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Início</label>
                        <input type="time" className="form-control" value={form.hora_inicio}
                          onChange={e => setForm(f => ({ ...f, hora_inicio: e.target.value }))} />
                      </div>
                      <div className="col-md-2">
                        <label className="form-label">Término</label>
                        <input type="time" className="form-control" value={form.hora_fim}
                          onChange={e => setForm(f => ({ ...f, hora_fim: e.target.value }))} />
                      </div>
                      <div className="col-md-3">
                        <label className="form-label">Qtd Horas *</label>
                        <input type="number" className="form-control" required min="0.5" max="24" step="0.5"
                          placeholder="Ex: 2"
                          value={form.quantidade_horas}
                          onChange={e => setForm(f => ({ ...f, quantidade_horas: e.target.value }))} />
                      </div>
                    </>
                  )}

                  {/* CID / Benefício conforme motivo */}
                  {MOSTRAR_CID.includes(form.motivo) && (
                    <div className="col-md-4">
                      <label className="form-label">CID</label>
                      <input className="form-control" placeholder="Ex: J00" value={form.cid}
                        onChange={e => setForm(f => ({ ...f, cid: e.target.value }))} />
                    </div>
                  )}
                  {MOSTRAR_BENEFICIO.includes(form.motivo) && (
                    <div className="col-md-5">
                      <label className="form-label">Nº Benefício INSS</label>
                      <input className="form-control" value={form.numero_beneficio}
                        onChange={e => setForm(f => ({ ...f, numero_beneficio: e.target.value }))} />
                    </div>
                  )}

                  {/* Observação */}
                  <div className="col-12">
                    <label className="form-label">Observação</label>
                    <textarea className="form-control" rows={2} value={form.observacao}
                      onChange={e => setForm(f => ({ ...f, observacao: e.target.value }))} />
                  </div>
                </div>
              </div>
              <div className="modal-footer-custom">
                <button type="button" className="btn-secondary-custom" onClick={() => setModal(false)}>
                  Cancelar
                </button>
                <button type="submit" className="btn-primary-custom" disabled={loading || !form.funcionario}>
                  {loading ? <span className="spinner-border spinner-border-sm" /> : <><i className="bi bi-check-lg" />Salvar</>}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
